
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Booking } from "./types";
import { formatTime, getStatusColor } from "./utils";
import { BankTransferDialog } from "./BankTransferDialog";
import { useState } from "react";

interface BookingCardProps {
  booking: Booking;
  selectedBookingId: string | null;
  isConfirmingPayment: boolean;
  onCancelBooking: (bookingId: string) => Promise<void>;
  onConfirmPayment: (bookingId: string) => Promise<void>;
}

export const BookingCard = ({
  booking,
  selectedBookingId,
  isConfirmingPayment,
  onCancelBooking,
  onConfirmPayment,
}: BookingCardProps) => {
  const [showCareemConfirm, setShowCareemConfirm] = useState(false);

  const handleCareemPayClick = () => {
    window.open(`https://cpay.me/971521006162?amount=${booking.amount}`, '_blank');
    setShowCareemConfirm(true);
  };

  const renderPaymentButtons = () => {
    if (showCareemConfirm) {
      return (
        <div className="mt-4">
          <Button 
            onClick={() => onConfirmPayment(booking.id)}
            disabled={isConfirmingPayment}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {isConfirmingPayment ? "Confirming..." : "Confirm Payment"}
          </Button>
        </div>
      );
    }

    return (
      <div className="mt-4 flex gap-4">
        <Button 
          variant="default" 
          className="w-[50px] h-[50px] p-0 bg-[#1A1F2C] hover:bg-[#1A1F2C]/90 rounded-xl overflow-hidden"
          onClick={handleCareemPayClick}
        >
          <img 
            src="/lovable-uploads/17f64a7a-f530-4ca1-af23-552106035ff1.png" 
            alt="Pay with Careem Pay"
            className="w-full h-full object-contain bg-black"
          />
        </Button>

        <BankTransferDialog
          bookingId={booking.id}
          onConfirmPayment={onConfirmPayment}
          isConfirmingPayment={isConfirmingPayment}
        />
      </div>
    );
  };

  return (
    <div key={booking.id} className="p-4 bg-gray-50 rounded-lg relative">
      <h3 className="font-medium">
        {format(new Date(booking.time_slot.date), "EEEE, MMMM d, yyyy")}
        {" at "}
        {formatTime(booking.time_slot.start_time)}
      </h3>
      <p className="text-sm text-gray-600 mt-1">
        {booking.guest_count} guest{booking.guest_count !== 1 ? 's' : ''} • {booking.amount} AED
      </p>
      {booking.guests && booking.guests.length > 0 && (
        <div className="mt-2">
          <p className="text-sm text-gray-600">Guests:</p>
          <ul className="list-disc list-inside text-sm text-gray-600 ml-2">
            {booking.guests.map((guest, index) => (
              <li key={index}>
                {guest.name} - {guest.skillLevel}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className={`text-sm mt-1 ${getStatusColor(booking.status)}`}>
        Status: {booking.status}
      </p>

      {booking.status === 'pending payment' && selectedBookingId !== booking.id && (
        renderPaymentButtons()
      )}
      
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button className="absolute bottom-4 right-4 text-sm text-red-600 hover:text-red-700">
            Cancel
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, keep booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onCancelBooking(booking.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, cancel booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
